import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { useGenerateCourseMutation } from "@/pages/Private/redux/courses/courses.api";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { usePageTitle } from "@/utils/usePageTitle";

import { TextArea } from "@/components/TextArea/TextArea";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { InputField } from "@/components/InputField/InputField";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { IStageContentInfo } from "../constants/emptyCourseScenario";
import { GenerateCourse, GenerateCourseSchema } from "../schema/courses";

import ContentModal from "../../Course/components/ContentModal";

export const GenerateCourseForm: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`courses.${key}`), [t]);

	usePageTitle(ts("generate.title"));
	const navigate = useNavigate();
	const [generateCourse, { isLoading }] = useGenerateCourseMutation();
	const [currentFormState, setCurrentFormState] = useState<Partial<GenerateCourse>>({
		industry: "",
		companySize: "",
		primaryIncidentType: "",
		specificCompanyDeficits: [],
		specificNotes: "",
		language: "German",
	});
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const [currentShowingContentInfo, setCurrentShowingContentInfo] =
		useState<IStageContentInfo | null>(null);

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<GenerateCourse>({
		defaultValues: {
			industry: "",
			companySize: "",
			primaryIncidentType: "",
			specificCompanyDeficits: [],
			specificNotes: "",
			language: "German",
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: zodResolver(GenerateCourseSchema) as any,
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof GenerateCourse) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const onSubmit = async (values: GenerateCourse) => {
		try {
			const result = await generateCourse({
				...values,
			}).unwrap();

			if (result?.message) {
				setShowSuccessModal(true);
			}
		} catch (err) {
			console.error(err);
		}
	};
	const handleClose = () => {
		navigate(-1);
	};

	const companySizeOptions: AutoCompleteItem[] = useMemo(
		() => [
			{
				title: ts("companySize.small"),
				id: "Small (1-50 employees)",
			},
			{
				title: ts("companySize.medium"),
				id: "Medium (51-500 employees)",
			},
			{
				title: ts("companySize.large"),
				id: "Large (501-4999 employees)",
			},
			{
				title: ts("companySize.veryLarge"),
				id: "Very Large (5000+ employees)",
			},
		],
		[ts]
	);

	const incidentTypeOptions: AutoCompleteItem[] = useMemo(
		() => [
			{
				title: ts("incidentType.cyberAttack"),
				id: "Cyber attack (e.g. ransomware, data breach)",
			},
			{
				title: ts("incidentType.naturalDisaster"),
				id: "Natural disaster (e.g. earthquake, hurricane, flood)",
			},
			{
				title: ts("incidentType.pandemic"),
				id: "Pandemic / health crisis",
			},
			{
				title: ts("incidentType.supplyChainDisruption"),
				id: "Significant disruption to the supply chain",
			},
			{
				title: ts("incidentType.utilityFailure"),
				id: "Failure of critical utilities (e.g. electricity, water, internet)",
			},
			{
				title: ts("incidentType.physicalSecurityIncident"),
				id: "Physical security incident (e.g. unauthorized access, sabotage)",
			},
			{
				title: ts("incidentType.terrorism"),
				id: "Terrorism / Civil unrest",
			},
			{
				title: ts("incidentType.equipmentFailure"),
				id: "Failure of critical equipment or infrastructure",
			},
			{
				title: ts("incidentType.personnelLoss"),
				id: "Sudden loss of key personnel",
			},
			{
				title: ts("incidentType.financialCrisis"),
				id: "Severe financial crisis / market crash",
			},
		],
		[ts]
	);

	const specific = useMemo(() => {
		return [
			{
				title: ts("specificDeficits.inadequatePlans"),
				id: "Inadequate emergency plans",
			},
			{
				title: ts("specificDeficits.overburdenedRoles"),
				id: "Overburdened key roles (e.g. CEO, crisis management team leader)",
			},
			{
				title: ts("specificDeficits.outdatedInfrastructure"),
				id: "Outdated IT infrastructure",
			},
			{
				title: ts("specificDeficits.lackOfCommunication"),
				id: "Lack of internal communication / silo thinking",
			},
			{
				title: ts("specificDeficits.supplierDependence"),
				id: "High dependence on individual suppliers",
			},
		];
	}, [ts]);

	const languageOptions: AutoCompleteItem[] = useMemo(
		() => [
			{ id: "English", title: ts("language.english") },
			{ id: "German", title: ts("language.deutsch") },
		],
		[ts]
	);

	return (
		<>
			<div className="flex flex-col h-[calc(100vh-6.063rem)]">
				<div className="flex-1  bg-brand-white border border-border shadow-custom-xs">
					<div className="flex justify-center w-full p-6">
						<Stack className="w-[65%] mb-5" spacing={"1rem"}>
							{" "}
							<InputField
								containerClassName=""
								error={!!errors.industry?.message}
								errorMessage={ts("generate.errors.industry")}
								handleChange={(e) => handleSaveFormState(e.target.value, "industry")}
								label={ts("industry")}
								name={"industry"}
								placeholder={ts("industry")}
								showError={!!errors.industry?.message}
								value={currentFormState?.industry || ""}
							/>
							<Dropdown
								floating
								data={companySizeOptions}
								error={!!errors.companySize?.message}
								errorMessage={ts("generate.errors.companySize")}
								handleSelect={(value) => handleSaveFormState(value?.title, "companySize")}
								label={ts("companySize-label")}
							/>
							<Dropdown
								floating
								data={incidentTypeOptions}
								error={!!errors.primaryIncidentType?.message}
								errorMessage={ts("generate.errors.primaryIncidentType")}
								handleSelect={(value) => handleSaveFormState(value?.title, "primaryIncidentType")}
								label={ts("primaryIncidentType")}
							/>
							<Dropdown
								floating
								data={languageOptions}
								defaultValue={languageOptions.find(
									(item) => item.id === currentFormState?.language
								)}
								handleSelect={(value) => handleSaveFormState(value?.id, "language")}
								label={ts("language.label")}
							/>
							<div className="mb-4">
								<label className="block mb-2 text-[0.875rem] font-bold text-primary-gray appearance-none">
									{ts("specificCompanyDeficits")}
								</label>
								<div className="flex flex-col gap-2">
									{specific.map((item) => (
										<Checkbox
											key={item.id}
											isChecked={!!currentFormState.specificCompanyDeficits?.includes(item.id)}
											onChange={() => {
												const prev = currentFormState.specificCompanyDeficits || [];
												const exists = prev.includes(item.id);
												const updated = exists
													? prev.filter((t) => t !== item.id)
													: [...prev, item.id];

												handleSaveFormState(updated, "specificCompanyDeficits");
											}}
										>
											{item.title}
										</Checkbox>
									))}
								</div>
							</div>
							<TextArea
								className="bg-white !h-full"
								containerClassName="!h-full overflow-auto custom-scrollbar"
								errorMessage={errors.specificNotes?.message}
								handleChange={(e) => handleSaveFormState(e.target.value, "specificNotes")}
								label={ts("specificNotes")}
								labelClassName="!mb-0"
								mainContainerClassName="!min-h-[15dvh] !max-h-[15dvh]" // Increased from 15dvh
								name="specificNotes"
								placeholder={ts("specificNotes-placeholder")}
								showError={false}
								value={currentFormState?.specificNotes || ""}
							/>
						</Stack>
					</div>
				</div>
				<div className="flex-shrink-0 bg-white border-t border-border">
					<Stack
						alignItems="center"
						direction="row"
						flexWrap="wrap"
						justifyContent="flex-end"
						spacing={2}
						sx={{ py: 2, pr: 4 }}
					>
						<div className="w-fit">
							<Button
								className="!h-[2.75rem]"
								color={ButtonColor.ACTION_SECONDARY}
								title={t("basics.cancel")}
								onClick={handleClose}
							/>
						</div>
						<div className="w-fit">
							<Button
								className="!h-[2.75rem]"
								color={ButtonColor.ACTION}
								disabled={isLoading}
								isLoading={isLoading}
								title={t("basics.generate")}
								onClick={handleSubmit(onSubmit)}
							/>
						</div>
					</Stack>
				</div>
			</div>
			<ContentModal
				contentInfo={currentShowingContentInfo}
				setCurrentContentInfo={setCurrentShowingContentInfo}
			/>
			{showSuccessModal && (
				<div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
					<div className="bg-white p-8 rounded shadow-lg w-full max-w-md text-center">
						<h1 className="text-3xl font-bold text-green-600 mb-4">{t("basics.success")}</h1>
						<p className="text-base text-gray-700 mb-6">{ts("scenario-generation-started")}</p>
						<div className="flex justify-center space-x-4">
							<Button
								title={t("basics.goHome")}
								onClick={() => navigate("/app/scenarios", { replace: true })}
							/>
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title={t("basics.close")}
								onClick={() => setShowSuccessModal(false)}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
